import { Link } from "@tanstack/react-router";
import { buttonVariants } from "@workspace/web-ui/components/button";
import { Card, CardContent } from "@workspace/web-ui/components/card";
import { cn } from "@workspace/web-ui/lib/utils";
import paymentError from "~/assets/images/consultation-payment-error.svg";
import paymentSuccess from "~/assets/images/consultation-payment-success.svg";
import { useAppointmentFormStore } from "../store/use-appointment-store";
import { useRescheduleAppointmentFormStore } from "../store/use-reschedule";
import { useSuspenseQuery } from "@tanstack/react-query";
import { appointmentQueryOptions } from "~/features/dashboard/pages/appointments/api/queries/appointment";
import { getConsultationQueryOptions } from "../api/queries/consultation";


export function RescheduleConsultationSuccessCard({ appointment_id }: { appointment_id: string }) {
  const { clearForm } = useRescheduleAppointmentFormStore()

  const { data } = useSuspenseQuery(appointmentQueryOptions(appointment_id))

  const consultationResponse = data?.appointment

  if (!consultationResponse) {
    return null
  }

  return (
    <div className="flex items-center mx-auto justify-center md:max-w-lg mt-10 bg-muted/50 px-4">
      <Card className=" w-auto rounded-xl shadow-xl">
        <CardContent className="p-6">
          <div className="flex flex-col w-full items-center text-center">
            <div className="size-20 mb-3">
              <img src={paymentSuccess} alt="payment-image" className="w-full" />
            </div>
            <h2 className="text-[35px] font-semibold">Appointment Rescheduled!</h2>
            <p className="text-sm flex text-center  w-full text-muted-foreground mt-1">
              This appointment has been successfully rescheduled! The patient will receive a confirmation email shortly with all the updated details.
            </p>

            <div className="w-full mt-6 text-sm text-left space-y-4">
              <div className="flex justify-between">
                <span className="#2D2F35">Date</span>
                <span className="text-[#202020CC]">{new Date(consultationResponse.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }).replace(/(\d+)(?:st|nd|rd|th)/, (_, d) => `${d}${['th', 'st', 'nd', 'rd'][d > 3 && d < 21 ? 0 : d % 10] || 'th'}`)}</span>
              </div>
              <div className="flex justify-between">
                <span className="#2D2F35">Time</span>
                <span className="text-[#202020CC]">{new Date(`2000-01-01T${consultationResponse.time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
              </div>
              <div className="flex justify-between">
                <span className="#2D2F35">Program</span>
                <span className="text-[#202020CC]">{consultationResponse.title}</span>
              </div>

            </div>

            <Link onClick={clearForm} to="/dashboard/weight-loss/appointments" className={cn(buttonVariants({ variant: "default" }), "mt-6 w-full rounded-lg bg-[#4272DD]")}>Return to dashboard</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ConsultationPaymentSuccessCard({ consultation_id }: { consultation_id: string | null }) {
  const { clearForm } = useAppointmentFormStore()
  const { data } = useSuspenseQuery(getConsultationQueryOptions(consultation_id ?? undefined))

  const consultationResponse = data;

  if (!consultationResponse) {
    return null;
  }

  return (
    <div className="flex items-center justify-center bg-muted/50 px-4 w-full">
      <Card className="w-full max-w-[95%] md:max-w-lg lg:max-w-xl rounded-xl shadow-xl">
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col items-center text-center">
        <div className="size-16 md:size-20 mb-2 md:mb-3">
          <img src={paymentSuccess} alt="payment-image" className="w-full" />
        </div>
        <h2 className="text-2xl md:text-[32px] font-semibold">Thanks for your order</h2>
        <p className="text-xs md:text-sm flex text-center w-full text-muted-foreground mt-1">
          Congratulations on taking this important step toward your weight loss journey!
        </p>

        <div className="w-full mt-4 md:mt-6 text-xs md:text-sm text-left space-y-3 md:space-y-4">
          <div className="flex justify-between">
            <span className="#2D2F35">Transaction date</span>
            <span className="text-[#202020CC] break-words max-w-[50%] text-right">
              {new Date(consultationResponse.encounter_datetime).toLocaleDateString('en-US', { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric' 
              })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="#2D2F35">Service</span>
            <span className="text-[#202020CC]">Weight loss consultation</span>
          </div>
          <div className="flex justify-between font-medium pb-2 border-b">
            <span className="#2D2F35">Amount:</span>
            <span className="text-[#202020CC]">${consultationResponse.cost}</span>
          </div>
        </div>

        <Link onClick={clearForm} to="/dashboard/weight-loss" className={cn(buttonVariants({ variant: "default" }), "mt-4 md:mt-6 w-full rounded-lg bg-[#4272DD]")}>Go to dashboard</Link>
        </div>
      </CardContent>
      </Card>
    </div>
  )
}

export function ConsultationPaymentErrorCard() {
  const { clearForm } = useAppointmentFormStore()

  return (
    <div className="flex items-center justify-center bg-muted/50 px-4 w-full">
      <Card className="w-full max-w-[95%] md:max-w-lg lg:max-w-xl rounded-xl shadow-xl">
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col items-center text-center">
        <div className="size-16 md:size-[100px] mb-2 md:mb-3">
          <img src={paymentError} alt="payment-image" className="w-full" />
        </div>
        <h2 className="text-2xl md:text-[32px] font-semibold">Payment Failed!</h2>
        <p className="text-xs md:text-sm flex w-full md:w-[80%] text-center text-muted-foreground mt-1">
          We couldn&apos;t process your payment. This could be due to insufficient funds, incorrect card details, or a temporary issue with the payment processor.
        </p>

        <Link onClick={clearForm} to="/dashboard/weight-loss" className={cn(buttonVariants({ variant: "default" }), "mt-4 md:mt-6 w-full rounded-lg bg-[#4272DD]")}>Go to dashboard</Link>
        <Link onClick={clearForm} to="/dashboard/weight-loss" className={cn(buttonVariants({ variant: "default" }), "mt-3 md:mt-4 w-full rounded-lg text-[#464B55] bg-white border-[#DCDEE3]")}>Retry Payment</Link>
        </div>
      </CardContent>
      </Card>
    </div>
  )
}
